
import { format } from "date-fns";
import { Client } from "@/types/invoice";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

export const generateInvoiceNumber = (client: Client | undefined, billingDate: Date): string => {
  if (!client) {
    console.warn(`No client found for invoice generation`);
    return 'UNKNOWN' + format(billingDate, 'MMMy').toUpperCase();
  }
  
  console.log("Generating invoice number for client:", client);
  
  const clientPrefix = client.short_name 
    ? client.short_name.toUpperCase().replace(/\s+/g, '').substring(0, 3)
    : client.name.toUpperCase().replace(/\s+/g, '').substring(0, 3);
  
  const month = format(billingDate, 'MMM').toUpperCase();
  const shortYear = format(billingDate, 'yy');
  
  const invoiceNumber = `${clientPrefix}${month}${shortYear}`;
  console.log("Generated invoice number:", invoiceNumber);
  
  return invoiceNumber;
};

export const checkClientBillingDetails = async (
  clientId: number,
  setMissingBillingDetails: (details: any) => void,
  toast: ReturnType<typeof useToast>["toast"],
  errorMessage: string
): Promise<boolean> => {
  try {
    const { data: clientData, error } = await supabase
      .from('clients')
      .select(`
        id,
        name,
        short_name,
        legal_name,
        billing_details
      `)
      .eq('id', clientId)
      .single();

    if (error) throw error;

    const billingDetails = clientData.billing_details as {
      currency?: string;
      address?: string;
      taxId?: string;
    } | null;

    const missingFields = [];
    
    if (!clientData.short_name) missingFields.push('Short Name');
    if (!clientData.legal_name) missingFields.push('Legal Name');
    if (!billingDetails?.currency) missingFields.push('Currency');
    if (!billingDetails?.address) missingFields.push('Billing Address');
    if (!billingDetails?.taxId) missingFields.push('Tax ID');

    if (missingFields.length > 0) {
      setMissingBillingDetails({
        clientId,
        clientName: clientData.name,
        missingFields
      });
      return false;
    }

    setMissingBillingDetails(null);
    return true;
  } catch (error) {
    console.error('Error checking client billing details:', error);
    toast({
      title: errorMessage,
      variant: "destructive",
    });
    return false;
  }
};
